<script>
import { mapActions, mapState } from 'vuex'

import TopologyDiagramIp from './TopologyDiagramIp.vue'

export default {
  components: {
    TopologyDiagramIp,
  },

  props: {
    topologyBlueprint: {
      type: Object
    },
    reservationTransaction: {
      type: Object,
    },
  },

  created () {
    if(this.image_tiers.length == 0) {
      this.getImageTiers()
    }
  },

  computed: {
    ...mapState({
      infrastructure_instances: state =>
        state.infrastructure.infrastructure_instances,
      image_blueprints: state =>
        state.image.image_blueprints,
      image_flavors: state =>
        state.image.image_flavors,
      image_groups: state =>
        state.image.image_groups,
      image_tiers: state =>
        state.image.image_tiers,
      topology_blueprints: state =>
        state.topology.topology_blueprints,
      topology_groups: state =>
        state.topology.topology_groups,
      topology_images: state =>
        state.topology.topology_images,
    }),

    isVisible: function () {
      return this.image_tiers.length > 0 &&
        (this.reservationTransaction || this.topologyBlueprint)
    },

    isDualPathTopology: function () {
      return this.topologyBlueprint.attributes.blueprint_wan_path_count == 2
    },

    branchNetworkImage: function () {
      return this.diagramImage(
        this.imageGroupByTier(1)
      )
    },

    branchOptimization: function () {
      return this.imageGroupByTier(2)
    },

    branchOptimizationImage: function () {
      return this.diagramImage(
        this.branchOptimization
      )
    },

    wan: function () {
      return this.imageGroupByTier(4)
    },

    wanSubnetStyle: function () {
      let topPosition = this.wan.attributes.group_path1_diagram_top
      let leftPosition = this.wan.attributes.group_path1_diagram_left

      if(this.isDualPathTopology) {
        topPosition = this.wan.attributes.group_path2_diagram_top
        leftPosition = this.wan.attributes.group_path2_diagram_left
      }

      return 'position: absolute; width: 100px; ' +
        'top: ' + topPosition + 'px; ' +
        'left: ' + leftPosition + 'px;'
    },

    wanImage: function () {
      if(this.isDualPathTopology) {
        return "/images/topologies/wan-dual.png"
      }

      return "/images/topologies/wan-single.png"
    },

    datacenterNetworkImage: function () {
      return this.diagramImage(
        this.imageGroupByTier(5)
      )
    },

    datacenterOptimization: function () {
      return this.imageGroupByTier(6)
    },

    datacenterOptimizationImage: function () {
      return this.diagramImage(
        this.datacenterOptimization
      )
    },
  },

  methods: {
    ...mapActions([
      'getImageTiers'
    ]),

    imageGroupByTier: function (tier_id) {
      let topology_group = this.topology_groups
        .filter(
          g => g.attributes.topology_blueprint_id == this.topologyBlueprint.id
        )
        .find(
          g => g.attributes.image_tier_id == tier_id
        )

      return this.image_groups.find(
        g => g.id == topology_group.attributes.image_group_id
      )
    },

    subnetStyle: function (imageGroup, path) {
      let topPosition = imageGroup.attributes.group_path1_diagram_top
      let leftPosition = imageGroup.attributes.group_path1_diagram_left

      if(path == 2) {
        topPosition = imageGroup.attributes.group_path2_diagram_top
        leftPosition = imageGroup.attributes.group_path2_diagram_left
      }

      return 'position: absolute; width: 100px; ' +
        'top: ' + topPosition + 'px; ' +
        'left: ' + leftPosition + 'px;'
    },

    diagramImage: function (group) {
      if(! group) {
        return ""
      }

      return "/images/topologies/" +
        group.attributes.group_alias +
        "-preview.png"
    },

    imagesByTier: function (tier_id) {
      let instance_type = 'topology_image'
      let instances = this.topology_images

      if(this.reservationTransaction) {
        instance_type = 'infrastructure_instance'
        instances = this.infrastructure_instances
      }

      return instances.map(function(instance) {
        let image_blueprint = this.image_blueprints.find(
          b => b.id == instance.attributes.image_blueprint_id
        )
        let image_flavor = this.image_flavors.find(
          f => f.id == instance.attributes.image_flavor_id
        )

        return {
          [instance_type]: instance,
          'topology_blueprint': this.topologyBlueprint,
          'image_blueprint': image_blueprint,
          'image_flavor': image_flavor
        }
      }, this)
      .filter(function (instance) {
        return instance.image_blueprint.attributes.image_tier_id == tier_id
      })
      .sort(function(a, b) {
          let textA = a.image_blueprint.attributes.blueprint_name.toUpperCase()
          let textB = b.image_blueprint.attributes.blueprint_name.toUpperCase()
          return (textA < textB) ? -1 : (textA > textB) ? 1 : 0
      })
    },

    isActiveStatus: function (vmInstance) {
      return vmInstance.infrastructure_instance &&
        vmInstance.infrastructure_instance.attributes.instance_status == 3
    },
  },
}
</script>

<template>
<div class="topology-diagram" v-if="isVisible">
  <!-- Branch Hosts -->
  <div class="topology-hosts">
    <h5>Branch Hosts</h5>

    <topology-diagram-ip v-for="vmInstance in imagesByTier(3)"
      :key="vmInstance.image_flavor.id"
      :vm-instance="vmInstance"
    >
    </topology-diagram-ip>
  </div>

  <div class="topology-images">

    <!-- Branch Optimization -->
    <div class="topology-image">
      <img :src="branchOptimizationImage" />

      <!-- Branch Optimization Subnet Path 1 -->
      <div class="topology-host"
        v-if="branchOptimization.attributes.group_path1_label && branchOptimization.attributes.group_path1_subnet"
        :style="subnetStyle(branchOptimization, 1)"
      >
        <div class="topology-host-name">
          <div class="status default"><div class="circle"></div></div>
          <div class="vm-name">
            {{ branchOptimization.attributes.group_path1_label }}
          </div>
        </div>
        <div class="topology-host-ips">
          <div class="topology-host-ip">
            <code style="margin-left: 0px;">{{ branchOptimization.attributes.group_path1_subnet }}</code>
          </div>
        </div>
      </div>

      <!-- Branch Optimization Subnet Path 2 -->
      <div class="topology-host"
        v-if="isDualPathTopology && branchOptimization.attributes.group_path2_label && branchOptimization.attributes.group_path2_subnet"
        :style="subnetStyle(branchOptimization, 2)"
      >
        <div class="topology-host-name">
          <div class="status default"><div class="circle"></div></div>
          <div class="vm-name">
            {{ branchOptimization.attributes.group_path2_label }}
          </div>
        </div>
        <div class="topology-host-ips">
          <div class="topology-host-ip">
            <code style="margin-left: 0px;">{{ branchOptimization.attributes.group_path2_subnet }}</code>
          </div>
        </div>
      </div>

      <topology-diagram-ip v-for="vmInstance in imagesByTier(2)"
        :key="vmInstance.image_flavor.id"
        :vm-instance="vmInstance"
      >
      </topology-diagram-ip>
    </div>

    <!-- Branch Network -->
    <div class="topology-image">
      <img :src="branchNetworkImage" />

      <topology-diagram-ip v-for="vmInstance in imagesByTier(1)"
        :key="vmInstance.image_flavor.id"
        :vm-instance="vmInstance"
      >
      </topology-diagram-ip>
    </div>

    <!-- WAN -->
    <div class="topology-image">
      <img :src="wanImage" />

      <div :style="wanSubnetStyle">
        <div class="topology-host"
          v-if="wan.attributes.group_path1_label && wan.attributes.group_path1_subnet"
        >
          <div class="topology-host-name">
            <div class="status default"><div class="circle"></div></div>
            <div class="vm-name">
              {{ wan.attributes.group_path1_label }}
            </div>
          </div>
          <div class="topology-host-ips">
            <div class="topology-host-ip">
              <code style="margin-left: 0px;">{{ wan.attributes.group_path1_subnet }}</code>
            </div>
          </div>
        </div>
        <div class="topology-host"
          v-if="isDualPathTopology && wan.attributes.group_path2_label && wan.attributes.group_path2_subnet"
        >
          <div class="topology-host-name">
            <div class="status default"><div class="circle"></div></div>
            <div class="vm-name">
              {{ wan.attributes.group_path2_label }}
            </div>
          </div>
          <div class="topology-host-ips">
            <div class="topology-host-ip">
              <code style="margin-left: 0px;">{{ wan.attributes.group_path2_subnet }}</code>
            </div>
          </div>
        </div>
      </div>

      <topology-diagram-ip v-for="vmInstance in imagesByTier(4)"
        :key="vmInstance.image_flavor.id"
        :vm-instance="vmInstance"
      >
      </topology-diagram-ip>
    </div>

    <!-- Datacenter Network -->
    <div class="topology-image">
      <img :src="datacenterNetworkImage" />

      <topology-diagram-ip v-for="vmInstance in imagesByTier(5)"
        :key="vmInstance.image_flavor.id"
        :vm-instance="vmInstance"
      >
      </topology-diagram-ip>
    </div>

    <!-- Datacenter Optimization -->
    <div class="topology-image">
      <img :src="datacenterOptimizationImage" />

      <!-- Datacenter Optimization Subnet Path 1 -->
      <div class="topology-host"
        v-if="datacenterOptimization.attributes.group_path1_label && datacenterOptimization.attributes.group_path1_subnet"
        :style="subnetStyle(datacenterOptimization, 1)"
      >
        <div class="topology-host-name">
          <div class="status default"><div class="circle"></div></div>
          <div class="vm-name">
            {{ datacenterOptimization.attributes.group_path1_label }}
          </div>
        </div>
        <div class="topology-host-ips">
          <div class="topology-host-ip">
            <code style="margin-left: 0px;">{{ datacenterOptimization.attributes.group_path1_subnet }}</code>
          </div>
        </div>
      </div>

      <!-- Datacenter Optimization Subnet Path 2 -->
      <div class="topology-host"
        v-if="isDualPathTopology && datacenterOptimization.attributes.group_path2_label && datacenterOptimization.attributes.group_path2_subnet"
        :style="subnetStyle(datacenterOptimization, 2)"
      >
        <div class="topology-host-name">
          <div class="status default"><div class="circle"></div></div>
          <div class="vm-name">
            {{ datacenterOptimization.attributes.group_path2_label }}
          </div>
        </div>
        <div class="topology-host-ips">
          <div class="topology-host-ip">
            <code style="margin-left: 0px;">{{ datacenterOptimization.attributes.group_path2_subnet }}</code>
          </div>
        </div>
      </div>

      <topology-diagram-ip v-for="vmInstance in imagesByTier(6)"
        :key="vmInstance.image_flavor.id"
        :vm-instance="vmInstance"
      >
      </topology-diagram-ip>
    </div>
  </div>

  <!-- Datacenter Hosts -->
  <div class="topology-hosts">
    <h5>Datacenter Hosts</h5>

    <topology-diagram-ip v-for="vmInstance in imagesByTier(7)"
      :key="vmInstance.image_blueprint.id"
      :vm-instance="vmInstance"
    >
    </topology-diagram-ip>
    <div v-if="imagesByTier(7).length == 0">
      No hosts have been added.
    </div>
  </div>
</div>
</template>
